/**
 * inputTracker.ts — Per-run input-mix accounting
 *
 * BETA 16.71 — Per Dylan: "we need to know what is working and what
 * isnt working and it is all trackable. i wat to be able to play this
 * game on my xbox rog ally x." Tracks how much of a run was driven by
 * keyboard vs touch vs gamepad vs mouse so PSBOT can analyze
 * input-mode-specific bug patterns (e.g. "all our crashes happen on
 * touch" or "controller players survive longer than keyboard").
 *
 * Approach: each frame, the engine calls noteInputActivity(source) for
 * any source that contributed THIS frame (movement / interact / Q / etc).
 * Per-source second accumulators tick using dt. At run end, the live
 * tracker reads totals + percent breakdown.
 *
 * "Active" frames are frames where the source actually drove SOMETHING
 * (a key was held, a touch knob was active, a gamepad stick was past
 * deadzone). Idle frames don't count toward any source — so a player
 * who plays 100% with controller will have ~controllerSeconds = wall
 * time minus pause/idle, with the other sources at 0.
 */

export type InputSource = 'keyboard' | 'mouse' | 'touch' | 'gamepad';

interface InputState {
  totals: Record<InputSource, number>; // seconds active per source
  // Frame-scoped flags: set during the frame, consumed at frame end.
  // Letting multiple sources fire in the same frame counts toward all
  // of them (a player with a controller in their right hand and a
  // mouse on the desk who fires a click DURING gamepad movement gets
  // both keyboards-mouse + controller-second this frame).
  thisFrame: Record<InputSource, boolean>;
}

const _state: InputState = {
  totals: { keyboard: 0, mouse: 0, touch: 0, gamepad: 0 },
  thisFrame: { keyboard: false, mouse: false, touch: false, gamepad: false },
};

/** Reset on run start. */
export function resetInputTracker(): void {
  _state.totals.keyboard = 0;
  _state.totals.mouse = 0;
  _state.totals.touch = 0;
  _state.totals.gamepad = 0;
  _state.thisFrame.keyboard = false;
  _state.thisFrame.mouse = false;
  _state.thisFrame.touch = false;
  _state.thisFrame.gamepad = false;
}

/** Mark "this source produced input THIS frame". Cheap. Idempotent —
 *  calling twice in the same frame still only counts as one frame for
 *  that source. */
export function noteInputActivity(source: InputSource): void {
  _state.thisFrame[source] = true;
}

/** Per-frame commit: consume the frame's active-source flags into the
 *  per-source second totals, then clear flags for next frame. Call once
 *  per game tick after all input has been processed. */
export function tickInputTracker(dt: number): void {
  if (_state.thisFrame.keyboard) _state.totals.keyboard += dt;
  if (_state.thisFrame.mouse)    _state.totals.mouse    += dt;
  if (_state.thisFrame.touch)    _state.totals.touch    += dt;
  if (_state.thisFrame.gamepad)  _state.totals.gamepad  += dt;
  _state.thisFrame.keyboard = false;
  _state.thisFrame.mouse = false;
  _state.thisFrame.touch = false;
  _state.thisFrame.gamepad = false;
}

export interface InputMixSnapshot {
  keyboardSec: number;
  mouseSec: number;
  touchSec: number;
  gamepadSec: number;
  // Percentages — sum may exceed 100 because multiple sources can be
  // active in the same frame. Useful for "mostly controller, some
  // keyboard" patterns rather than enforcing a strict mutually-exclusive
  // breakdown.
  keyboardPct: number;
  mousePct: number;
  touchPct: number;
  gamepadPct: number;
  /** Single label naming the dominant input mode for grouping. */
  primary: InputSource | 'mixed' | 'idle';
}

/** Snapshot for telemetry. */
export function getInputMixSnapshot(): InputMixSnapshot {
  const t = _state.totals;
  const total = Math.max(0.001, t.keyboard + t.mouse + t.touch + t.gamepad);
  const round1 = (n: number) => Math.round(n * 10) / 10;
  const pct = (n: number) => Math.round((n / total) * 100 * 10) / 10;
  // Primary = the source with > 60% share, else "mixed". If all zero, "idle".
  let primary: InputSource | 'mixed' | 'idle' = 'idle';
  if (total > 0.5) {
    const entries = (Object.keys(t) as InputSource[]).map((k) => ({ k, v: t[k] }));
    entries.sort((a, b) => b.v - a.v);
    const top = entries[0];
    primary = top.v / total >= 0.6 ? top.k : 'mixed';
  }
  return {
    keyboardSec: round1(t.keyboard),
    mouseSec: round1(t.mouse),
    touchSec: round1(t.touch),
    gamepadSec: round1(t.gamepad),
    keyboardPct: pct(t.keyboard),
    mousePct: pct(t.mouse),
    touchPct: pct(t.touch),
    gamepadPct: pct(t.gamepad),
    primary,
  };
}
